import classNames from "classnames";
import { FC, ReactNode, useId } from "react";
import { FieldError } from "react-hook-form";
import useI18n from "../../hooks/useTranslations";
import SvgQuestionMark from "../icons/SvgQuestionMark";
import styles from "./FormField.module.css";
import Icon from "./Icon";

interface FormFieldProps {
  label?: string;
  children:
    | ReactNode
    | ((props: { labelId: string; isOptional: boolean; disabled: boolean; isInvalid: boolean }) => ReactNode);
  showHelp?: {
    type?: "info" | "warning" | "error";
    onOpen?: () => void;
  };
  isOptional?: boolean;
  disabled?: boolean;
  error?: FieldError;
  className?: string;
  hidden?: boolean;
  fullWidth?: boolean;
  warning?: string;
}

const FormField = ({
  label,
  showHelp,
  children,
  isOptional = false,
  disabled = false,
  error,
  className,
  hidden,
  fullWidth,
  warning,
}: FormFieldProps) => {
  const labelId = useId();
  const isInvalid = error !== undefined;

  return (
    <div
      className={classNames(styles.field, className, {
        [styles.disabled]: disabled,
        [styles.hidden]: hidden,
        [styles.full]: fullWidth,
      })}
    >
      {label && (
        <label htmlFor={labelId} className={styles.label}>
          {label}
          {!isOptional && <span className={styles.mandatory}> *</span>}
          {!!showHelp && (
            <Icon
              glyph={SvgQuestionMark}
              className={classNames(styles.help, { [styles.disabled]: !showHelp.onOpen })}
              onClick={showHelp.onOpen}
              fill={showHelp.type === "error" ? "var(--red)" : undefined}
            />
          )}
        </label>
      )}
      <div className={styles.data}>
        {typeof children === "function"
          ? children({
              labelId,
              isOptional,
              disabled,
              isInvalid,
            })
          : children}
        {isInvalid && <ErrorMessage message={error.message} type={error.type} />}
        {!!warning && <span className={classNames(styles.message, styles.warning)}>{warning}</span>}
      </div>
    </div>
  );
};

const ErrorMessage: FC<FieldError> = ({ message, type }: FieldError) => {
  const i18n = useI18n();

  const getErrorMessage = () => {
    if (message) return message;

    switch (type) {
      case "required":
        return i18n.translation.common.fieldCannotBeEmpty;
      case "min":
        return i18n.translation.common.fieldContainsInvalidValueInsertBiggerOne;
      case "max":
        return i18n.translation.common.fieldContainsInvalidValueInsertSmallerOne;
      case "pattern":
        return i18n.translation.common.fieldValueInvalidFormat;
      case "validate":
        return i18n.translation.common.fieldInvalidValue;
      default:
        return "";
    }
  };

  return <span className={classNames(styles.message, styles.error)}>{getErrorMessage()}</span>;
};

FormField.ErrorMessage = ErrorMessage;

export default FormField;
